import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Egg, Pokemon } from '../lib/types';
import { Bar, ShinyChip, TypeChip } from './ui';
import { EggArt } from './EggArt';
import { dexNumber, formatDuration, genderSuffix } from '../lib/format';

/* --- Egg ------------------------------------------------------------------ */

/**
 * One incubator slot. Ticks its own countdown locally between server reads —
 * the server already told us the seconds-per-step, so the bar can advance
 * smoothly without polling.
 */
export function EggSlot({
  egg,
  onHatch,
  onClick,
  busy,
}: {
  egg: Egg;
  onHatch?: (egg: Egg) => void;
  onClick?: (egg: Egg) => void;
  busy?: boolean;
}) {
  const [progress, setProgress] = useState(egg.progress);

  useEffect(() => setProgress(egg.progress), [egg.progress, egg.id]);

  useEffect(() => {
    if (egg.status !== 'INCUBATING' || progress >= egg.requiredProgress) return;
    const timer = window.setInterval(
      () => setProgress((current) => Math.min(egg.requiredProgress, current + 1)),
      egg.stepSeconds * 1000,
    );
    return () => window.clearInterval(timer);
  }, [egg.status, egg.requiredProgress, egg.stepSeconds, progress]);

  const ready = progress >= egg.requiredProgress && egg.status === 'INCUBATING';
  const remaining = (egg.requiredProgress - progress) * egg.stepSeconds;

  return (
    <div className={`pxframe egg-slot ${ready ? 'egg-slot--ready' : ''}`}>
      <button
        type="button"
        onClick={() => (ready && onHatch ? onHatch(egg) : onClick?.(egg))}
        disabled={busy}
        style={{ background: 'none', border: 0, padding: 0, cursor: ready ? 'pointer' : 'default' }}
        aria-label={ready ? `Hatch ${egg.familyName}` : `${egg.familyName}, ${Math.round((progress / egg.requiredProgress) * 100)}% incubated`}
      >
        <EggArt appearance={egg.appearance} state={ready ? 'ready' : 'idle'} label={egg.familyName} />
      </button>

      <div className="mono tiny">{egg.familyName}</div>

      {ready ? (
        <>
          <span className="egg-slot__ready-tag">READY!</span>
          {onHatch && (
            <button className="btn btn--gold btn--sm" onClick={() => onHatch(egg)} disabled={busy}>
              Hatch it!
            </button>
          )}
        </>
      ) : (
        <div style={{ width: '100%' }} className="stack-sm">
          <Bar ratio={progress / egg.requiredProgress} variant="egg" />
          <div className="tiny muted center">
            {progress}/{egg.requiredProgress} · {formatDuration(remaining)}
          </div>
        </div>
      )}
    </div>
  );
}

export function EmptyEggSlot({ onClick, locked }: { onClick?: () => void; locked?: boolean }) {
  return (
    <button
      type="button"
      className="pxwell egg-slot egg-slot--empty"
      onClick={onClick}
      disabled={locked}
      style={{ border: 0, width: '100%' }}
    >
      <div className="egg-slot__plus" aria-hidden>
        {locked ? '🔒' : '+'}
      </div>
      <div className="tiny mono">{locked ? 'Locked slot' : 'Place an egg'}</div>
    </button>
  );
}

/* --- Pokemon card --------------------------------------------------------- */

export function PokemonCard({
  pokemon,
  to,
  onClick,
  footer,
  showLevel = true,
}: {
  pokemon: Pokemon;
  to?: string;
  onClick?: () => void;
  footer?: ReactNode;
  showLevel?: boolean;
}) {
  const inner = (
    <>
      <div className="pkmn-card__art">
        {pokemon.isShiny && (
          <span className="shiny-star" aria-hidden>
            ✦
          </span>
        )}
        <img className="pixel" src={pokemon.sprite} alt="" loading="lazy" />
      </div>
      <div className="pkmn-card__name">
        {pokemon.displayName}
        {genderSuffix(pokemon.displayName, pokemon.gender) && (
          <span
            className="muted"
            style={{ marginLeft: 3, color: pokemon.gender === 'female' ? 'var(--berry)' : 'var(--sky-dark)' }}
          >
            {genderSuffix(pokemon.displayName, pokemon.gender)}
          </span>
        )}
      </div>
      {showLevel && <div className="pkmn-card__meta">Lv. {pokemon.level}</div>}
      <div className="pkmn-card__types">
        {pokemon.species.types.map((type) => (
          <TypeChip key={type} type={type} />
        ))}
      </div>
      {footer}
    </>
  );

  const className = `pxframe pkmn-card ${pokemon.isShiny ? 'pkmn-card--shiny' : ''}`;

  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      {inner}
    </button>
  );
}

/* --- Pokemon identity header (detail + Team) ------------------------------ */

export function PokemonIdentity({ pokemon }: { pokemon: Pokemon }) {
  return (
    <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
      <div style={{ position: 'relative' }}>
        <img
          className="pixel"
          src={pokemon.sprite}
          alt={pokemon.displayName}
          style={{ width: 128, height: 128, objectFit: 'contain' }}
        />
      </div>
      <div className="stack-sm" style={{ flex: 1, minWidth: 200 }}>
        <div className="row row--wrap">
          <h2 style={{ fontSize: 'var(--text-xl)' }}>{pokemon.displayName}</h2>
          {pokemon.isShiny && <ShinyChip />}
        </div>
        <div className="tiny muted mono">
          {dexNumber(pokemon.species.id)} {pokemon.species.displayName}
          {pokemon.nickname ? '' : ''}
        </div>
        <div className="row row--wrap">
          {pokemon.species.types.map((type) => (
            <TypeChip key={type} type={type} />
          ))}
          {pokemon.nature && <span className="chip">{pokemon.nature.name}</span>}
          {genderSuffix(pokemon.displayName, pokemon.gender) && (
            <span className="chip">{genderSuffix(pokemon.displayName, pokemon.gender)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* --- EXP bar with numbers ------------------------------------------------- */

export function ExpBar({ pokemon }: { pokemon: Pokemon }) {
  return (
    <div className="stack-sm">
      <div className="row row--between tiny mono">
        <span>Level {pokemon.level}</span>
        <span className="muted">
          {pokemon.isMaxLevel
            ? 'MAX'
            : `${pokemon.expIntoLevel.toLocaleString()} / ${pokemon.expForNextLevel.toLocaleString()} EXP`}
        </span>
      </div>
      <Bar ratio={pokemon.expRatio} variant="exp" />
    </div>
  );
}
